import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const LOGO = "https://customer-assets.emergentagent.com/job_9b5e32bd-6e03-4600-841f-a63162f850e8/artifacts/w487gkes_lexorbit-logo.png.png";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_legal-management-pro/artifacts/0g008ewu_Crear_un_video_de_presentaci%C3%B3n.mp4";

export default function HeroSection({ onTrialClick }) {
  return (
    <section id="inicio" className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#050505]" data-testid="hero-section">
      <video autoPlay muted loop playsInline poster={LOGO} className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video">
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Overlay — stronger left for text legibility */}
      <div className="absolute inset-0 z-[1]" style={{
        background: `linear-gradient(90deg, rgba(5,5,5,0.88) 0%, rgba(5,5,5,0.72) 45%, rgba(5,5,5,0.60) 100%), radial-gradient(circle at 75% 40%, rgba(201,164,76,0.10), transparent 50%)`
      }} />
      <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-[#050505] to-transparent z-[1]" />

      {/* Particles */}
      {[...Array(5)].map((_, i) => (
        <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full bg-[#C9A44C] z-[2]"
          style={{ top: `${15 + (i * 14) % 55}%`, left: `${12 + (i * 19) % 75}%` }}
          animate={{ opacity: [0, 0.35, 0], y: [0, -16, 0] }}
          transition={{ duration: 5 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }} />
      ))}

      {/* Content — left-aligned, max 680px */}
      <div className="relative z-[3] w-full max-w-[680px] px-8 sm:px-12 lg:pl-20 xl:pl-24 py-36 lg:py-40">

        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="text-[#F8F6F0] leading-[1.05] tracking-tight mb-6"
          style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(48px, 6vw, 82px)' }}
          data-testid="hero-title">
          Gestión jurídica segura para{' '}<span className="text-[#C9A44C] italic">estudios modernos</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-[#D8D1C3] leading-[1.65] max-w-[620px] mb-4"
          style={{ fontSize: 'clamp(16px, 1.3vw, 20px)' }}>
          Creá tu organización en LexOrbit y accedé a un sistema de gestión legal completo para administrar causas, clientes, documentos, agenda, tareas y equipo desde un entorno privado.
        </motion.p>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="text-[#C9A44C]/80 text-xs tracking-[0.13em] uppercase mb-9">
          Sistema actualmente utilizado por estudios jurídicos reales
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.5 }}
          className="flex flex-col sm:flex-row gap-3 mb-10">
          <button onClick={onTrialClick}
            className="group text-[#050505] px-8 py-4 rounded text-[15px] font-semibold transition-all duration-300 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)', boxShadow: '0 0 28px rgba(201,164,76,0.28)' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 45px rgba(201,164,76,0.45)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 28px rgba(201,164,76,0.28)'}
            data-testid="hero-trial-btn">
            Probar gratis 5 días <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <a href="#planes"
            className="text-[#F8F6F0] px-8 py-4 rounded text-[15px] font-medium transition-all duration-300 text-center hover:bg-white/[0.06]"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,164,76,0.4)' }}
            data-testid="hero-plans-btn">Ver planes</a>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9 }}
          className="flex flex-wrap gap-x-5 gap-y-2 text-[#B8B8B8]/85 text-[10px] uppercase tracking-[0.14em]">
          {['Demo gratis 5 días', 'Datos separados por organización', 'Sistema ya operativo', 'Planes para abogados y estudios'].map((t, i) => (
            <span key={i} className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#C9A44C]" />{t}</span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
